import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import PasswordGate from './app/components/PasswordGate';
import quotrLogo from './assets/quotr-woordmerk.svg';
// Initialize i18n
import i18n from './i18n';
import config from './services/configService';
import { captureParentOrigin } from './services/parentMessenger';
import sentryService from './services/sentryService';
import type { ConfigData } from './types/app';

// CSS: All styles consolidated in main.css
import '@fontsource/inter';
import './styles/main.css';

// Initialize FontAwesome library
import './lib/fontawesome';

interface FnlConfig {
	currentConfig: ConfigData;
}

// Initialize error tracking
sentryService.init();

// Capture the iframe parent origin from the URL (`?parentOrigin=…`) once at
// startup so lead-tracking postMessage calls have a target — see
// `services/parentMessenger.ts` for the allowlist + protocol.
captureParentOrigin();

const initializeApp = async () => {
	const rootElement = document.getElementById('root');
	if (!rootElement) {
		throw new Error('Root element not found');
	}
	const root = ReactDOM.createRoot(rootElement);

	/**
	 * Renders the main app with the given configuration
	 */
	const renderApp = (fnlConfig: FnlConfig) => {
		root.render(
			<React.StrictMode>
				<PostHogProvider
					apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
					options={{
						api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
						defaults: '2025-05-24',
						capture_exceptions: true,
						debug: import.meta.env.MODE === 'development',
						persistence: 'memory'
					}}
				>
					<BrowserRouter>
						<App fnlConfig={fnlConfig} />
					</BrowserRouter>
				</PostHogProvider>
			</React.StrictMode>
		);
	};

	/**
	 * Callback after successful password authentication
	 * Initializes config and renders the app
	 */
	const handleAuthenticated = (_token: string, configData: ConfigData): void => {
		const fnlConfig = config.initWithAuthenticatedConfig(configData);
		renderApp(fnlConfig as FnlConfig);
	};

	/**
	 * Renders the password gate for protected labels
	 */
	const renderPasswordGate = () => {
		const { labelName } = config.extractLabelFromHostname();
		root.render(
			<React.StrictMode>
				<PasswordGate labelName={labelName} onAuthenticated={handleAuthenticated} />
			</React.StrictMode>
		);
	};

	/**
	 * Renders a generic error message
	 */
	const renderErrorScreen = () => {
		root.render(
			<div
				className="flex items-center justify-center min-h-screen"
				data-testid="initialization-error"
			>
				<div className="text-center">
					<img src={quotrLogo} alt="quotr" className="mx-auto mb-3 h-12 w-auto" />
					<small className="text-muted-foreground">{i18n.t('errors.initializationError')}</small>
				</div>
			</div>
		);
	};

	try {
		// Try to initialize normally (without password protection)
		const fnlConfig = await config.init(window.location.href);
		if (!fnlConfig?.currentConfig) {
			throw new Error('Failed to initialize application configuration');
		}
		renderApp(fnlConfig as FnlConfig);
	} catch (error) {
		// Handle password protection requirement
		if (error instanceof Error && error.message === 'PASSWORD_REQUIRED') {
			renderPasswordGate();
		} else {
			// Show generic error for other failures
			renderErrorScreen();
		}
	}
};

initializeApp();
